import { types as sdkTypes } from './sdkLoader';
import { sameOriginApiBase } from './sameOriginApiBase';

const { UUID, LatLng, Money } = sdkTypes;

const FILTER_KEYS_SKIP = new Set([
  'bounds',
  'origin',
  'page',
  'mapSearch',
  'zoom',
  'address',
  'sort',
]);

const readLatLng = point => {
  if (!point) return null;
  const lat = typeof point.lat === 'function' ? point.lat() : Number(point.lat);
  const lng = typeof point.lng === 'function' ? point.lng() : Number(point.lng);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  return { lat, lng };
};

const snapCoord = n => Math.round(Number(n) * 100) / 100;

/** Filters that change which events exist, ignoring pan, zoom, and page. */
export const mapClusterFilterKey = locationSearch => {
  const raw = String(locationSearch || '').replace(/^\?/, '');
  const params = new URLSearchParams(raw);
  const entries = [];
  params.forEach((value, key) => {
    if (!FILTER_KEYS_SKIP.has(key)) {
      entries.push(`${key}=${value}`);
    }
  });
  entries.sort();
  return entries.join('&');
};

const boundsRequestKey = bounds => {
  const ne = readLatLng(bounds && bounds.ne);
  const sw = readLatLng(bounds && bounds.sw);
  if (!ne || !sw) return '';
  return `${snapCoord(ne.lat)},${snapCoord(ne.lng)},${snapCoord(sw.lat)},${snapCoord(sw.lng)}`;
};

const rehydrateListing = listing => {
  if (!listing || !listing.id || !listing.attributes) return null;
  const price = listing.attributes.price || { amount: 0, currency: 'USD' };
  const geo = listing.attributes.geolocation;
  if (!geo || !Number.isFinite(Number(geo.lat)) || !Number.isFinite(Number(geo.lng))) {
    return null;
  }
  return {
    ...listing,
    id: new UUID(listing.id.uuid),
    attributes: {
      ...listing.attributes,
      price: new Money(Math.round(Number(price.amount) || 0), price.currency || 'USD'),
      geolocation: new LatLng(Number(geo.lat), Number(geo.lng)),
      publicData: listing.attributes.publicData || {},
    },
    images: (listing.images || []).map(image => ({
      ...image,
      id: image && image.id && image.id.uuid ? new UUID(image.id.uuid) : image && image.id,
    })),
  };
};

export const rehydrateMapClusterMarkers = markers => {
  if (!Array.isArray(markers)) return [];
  const out = [];
  markers.forEach(marker => {
    if (!marker) return;
    if (marker.type === 'cluster') {
      const lat = Number(marker.lat);
      const lng = Number(marker.lng);
      const count = Number(marker.count) || 0;
      if (!Number.isFinite(lat) || !Number.isFinite(lng) || count < 2) return;
      out.push({
        type: 'cluster',
        id: `c:${lat.toFixed(3)}:${lng.toFixed(3)}:${count}`,
        lat,
        lng,
        count,
        expansionZoom: Number(marker.expansionZoom) || 4,
      });
      return;
    }
    if (marker.type === 'group') {
      const listings = (marker.listings || []).map(rehydrateListing).filter(Boolean);
      const first = listings[0];
      const geo = first && first.attributes && first.attributes.geolocation;
      if (!first || !geo) return;
      out.push({
        type: 'group',
        id: `g:${first.id.uuid}:${marker.count || listings.length}`,
        count: Number(marker.count) || listings.length,
        listings,
        lat: geo.lat,
        lng: geo.lng,
      });
      return;
    }
    if (marker.type === 'point') {
      const listing = rehydrateListing(marker.listing);
      const geo = listing && listing.attributes && listing.attributes.geolocation;
      if (!listing || !geo) return;
      out.push({
        type: 'point',
        id: listing.id.uuid,
        listing,
        lat: geo.lat,
        lng: geo.lng,
      });
    }
  });
  return out;
};

export const fetchDiscoveryMapClusters = ({ locationSearch, bounds, zoom, signal }) => {
  const base = sameOriginApiBase();
  if (!base) {
    return Promise.reject(new Error('Cannot determine map cluster URL (no origin)'));
  }
  const raw = String(locationSearch || '').replace(/^\?/, '');
  const params = new URLSearchParams(raw);
  const ne = readLatLng(bounds && bounds.ne);
  const sw = readLatLng(bounds && bounds.sw);
  if (ne && sw) {
    params.set('bounds', `${ne.lat},${ne.lng},${sw.lat},${sw.lng}`);
  }
  params.set('zoom', String(Math.round(Number(zoom))));
  params.delete('page');
  const url = `${base}/api/discovery/map-clusters?${params.toString()}`;

  return fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: { Accept: 'application/json' },
    signal,
  }).then(res => {
    if (!res.ok) {
      return res.text().then(text => {
        throw new Error(text || `Map clusters failed (${res.status})`);
      });
    }
    return res.json();
  });
};

/**
 * Translate a cluster-session update into SearchMap state.
 * Returns null when the previous markers should stay on screen.
 */
export const clusterSessionState = update => {
  if (!update) return null;
  if (update.status === 'loading' && update.cleared) {
    return { mapClusters: [], mapClusterStatus: 'loading' };
  }
  if (update.status === 'loading') {
    return null;
  }
  if (update.status === 'ready') {
    return { mapClusters: update.markers || [], mapClusterStatus: 'ready' };
  }
  if (update.status === 'error') {
    return { mapClusterStatus: 'error' };
  }
  return null;
};

/**
 * One in-flight cluster request. Pans keep the previous bubbles until the next
 * response arrives. Filter changes clear them so stale counts don't linger.
 */
export const createDiscoveryMapClusterSession = onChange => {
  let seq = 0;
  let controller = null;
  let readyKey = null;
  let lastFilterKey = null;

  return {
    load({ locationSearch, bounds, zoom }) {
      const filterKey = mapClusterFilterKey(locationSearch);
      const zoomKey = Math.round(Number(zoom));
      const areaKey = boundsRequestKey(bounds);
      if (!areaKey || !Number.isFinite(zoomKey)) return;
      const key = `${filterKey}|${areaKey}|${zoomKey}`;
      if (key === readyKey) return;

      const requestId = ++seq;
      if (controller) controller.abort();
      controller = typeof AbortController !== 'undefined' ? new AbortController() : null;

      if (filterKey !== lastFilterKey) {
        lastFilterKey = filterKey;
        readyKey = null;
        onChange({ status: 'loading', markers: [], cleared: true });
      }

      fetchDiscoveryMapClusters({
        locationSearch,
        bounds,
        zoom: zoomKey,
        signal: controller ? controller.signal : undefined,
      })
        .then(json => {
          if (requestId !== seq) return;
          readyKey = key;
          onChange({
            status: 'ready',
            markers: rehydrateMapClusterMarkers(json && json.markers),
          });
        })
        .catch(err => {
          if (requestId !== seq) return;
          if (err && err.name === 'AbortError') return;
          readyKey = null;
          onChange({ status: 'error' });
        });
    },
    dispose() {
      seq += 1;
      readyKey = null;
      if (controller) controller.abort();
    },
  };
};
